import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Polyline, Marker, CircleMarker } from 'react-leaflet';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowLeft, MapPin, Clock, Gauge, Battery, Plane, RotateCw, CheckCircle2, AlertCircle, Trash2, Satellite, Map as MapIcon, FileText, Camera } from 'lucide-react';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { listFlights, removeFlight } from '../../lib/flightSyncDb';
import { flushQueue } from '../../lib/flightSyncApi';

// Fix leaflet default icons for webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

export default function FlightDetailPage({ ctx }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [flight, setFlight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    const all = await listFlights();
    setFlight(all.find((f) => f.id === id) || null);
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const handleSync = async () => {
    if (!navigator.onLine) return toast.error('Sem conexao');
    setSyncing(true);
    await flushQueue(ctx.token);
    await load();
    setSyncing(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Remover este voo?')) return;
    await removeFlight(id);
    toast.success('Removido');
    navigate('/flightsync/flights');
  };

  const { altData, speedData, batteryData, polyline, bounds } = useMemo(() => {
    if (!flight) return { altData: [], speedData: [], batteryData: [], polyline: [], bounds: null };
    const pts = flight.gps_track || [];
    // Downsample for charts when very large flights (>1500 pts) to keep Recharts snappy on low-end phones
    const CHART_MAX_POINTS = 1500;
    const step = pts.length > CHART_MAX_POINTS ? Math.ceil(pts.length / CHART_MAX_POINTS) : 1;
    const sampled = step > 1 ? pts.filter((_, i) => i % step === 0) : pts;
    const alt = [];
    const speed = [];
    const battery = [];
    let prev = null;
    let distCum = 0;
    const startTs = sampled[0]?.ts || 0;
    sampled.forEach((p, i) => {
      // time in seconds from start, or fallback to index
      const t = p.ts && startTs ? (p.ts - startTs) / 1000 : i;
      alt.push({ t: Math.round(t), altitude: p.alt || 0 });
      if (prev) {
        const d = haversine(prev.lat, prev.lng, p.lat, p.lng);
        const dt = p.ts && prev.ts ? (p.ts - prev.ts) / 1000 : 1;
        const sp = dt > 0 ? d / dt : 0;
        speed.push({ t: Math.round(t), speed: Number(sp.toFixed(2)) });
        distCum += d;
      }
      prev = p;
    });
    // Battery approximation: linear interpolation from start→end
    const bs = flight.battery_start_pct ?? null;
    const be = flight.battery_end_pct ?? null;
    if (bs !== null && be !== null && sampled.length > 1) {
      sampled.forEach((p, i) => {
        const t = p.ts && startTs ? (p.ts - startTs) / 1000 : i;
        const pct = bs + ((be - bs) * i) / (sampled.length - 1);
        battery.push({ t: Math.round(t), battery: Number(pct.toFixed(1)) });
      });
    }
    const polyline = pts.map((p) => [p.lat, p.lng]);
    const lats = pts.map((p) => p.lat);
    const lngs = pts.map((p) => p.lng);
    const bounds = lats.length
      ? [[Math.min(...lats) - 0.0005, Math.min(...lngs) - 0.0005],
         [Math.max(...lats) + 0.0005, Math.max(...lngs) + 0.0005]]
      : null;
    return { altData: alt, speedData: speed, batteryData: battery, polyline, bounds };
  }, [flight]);

  if (loading) {
    return <div data-testid="fs-flight-detail-loading" style={{ padding: 20, textAlign: 'center', color: 'var(--fs-text-dim)' }}>Carregando...</div>;
  }
  if (!flight) {
    return (
      <div data-testid="fs-flight-detail-not-found" style={{ padding: 20, textAlign: 'center' }}>
        <p style={{ color: 'var(--fs-text-dim)' }}>Voo nao encontrado</p>
        <button className="fs-btn fs-btn-ghost" onClick={() => navigate('/flightsync/flights')} style={{ marginTop: 12 }}>VOLTAR</button>
      </div>
    );
  }

  const tooltipStyle = {
    background: '#05060d',
    border: '1px solid rgba(0,212,255,0.3)',
    borderRadius: 8,
    color: '#e6f7ff',
    fontSize: 12,
  };

  return (
    <div data-testid="fs-flight-detail-page">
      <button
        onClick={() => navigate('/flightsync/flights')}
        className="fs-btn fs-btn-ghost"
        style={{ marginBottom: 12, width: 'auto', padding: '8px 14px', fontSize: 12 }}
        data-testid="fs-back-to-list"
      >
        <ArrowLeft size={14} /> VOLTAR
      </button>

      {/* Map */}
      {polyline.length > 1 ? (
        <div className="fs-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ height: 260, position: 'relative' }} data-testid="fs-flight-map">
            <MapContainer
              bounds={bounds}
              style={{ height: '100%', width: '100%', background: '#05060d' }}
              scrollWheelZoom={false}
              attributionControl={false}
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution="&copy; OpenStreetMap &copy; CartoDB"
              />
              <Polyline positions={polyline} pathOptions={{ color: '#00d4ff', weight: 3, opacity: 0.95 }} />
              <CircleMarker center={polyline[0]} radius={6} pathOptions={{ color: '#10b981', fillColor: '#10b981', fillOpacity: 1 }} />
              <CircleMarker center={polyline[polyline.length - 1]} radius={6} pathOptions={{ color: '#ef4444', fillColor: '#ef4444', fillOpacity: 1 }} />
            </MapContainer>
          </div>
        </div>
      ) : (
        <div className="fs-card">
          <p style={{ color: 'var(--fs-text-dim)', fontSize: 13, textAlign: 'center', margin: 0 }}>
            Trajetoria GPS indisponivel
          </p>
        </div>
      )}

      {/* Sync status + actions */}
      <div className="fs-card">
        <div className="fs-card-title" style={{ color: flight.synced ? 'var(--fs-green)' : 'var(--fs-amber)' }}>
          {flight.synced ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
          STATUS · {flight.synced ? 'SINCRONIZADO' : 'PENDENTE'}
        </div>
        <div className="fs-metric-row">
          <span className="fs-metric-label">Drone</span>
          <span className="fs-metric-value">{flight.drone_model || '-'}</span>
        </div>
        <div className="fs-metric-row">
          <span className="fs-metric-label">Arquivo</span>
          <span className="fs-metric-value" style={{ fontSize: 11 }}>{flight.file_name || '-'}</span>
        </div>
        {flight.server_flight_id && (
          <div className="fs-metric-row">
            <span className="fs-metric-label">ID servidor</span>
            <span className="fs-metric-value" style={{ fontSize: 11 }}>{flight.server_flight_id.slice(0, 12)}...</span>
          </div>
        )}
        {!flight.synced && (
          <button className="fs-btn fs-btn-primary" onClick={handleSync} disabled={syncing} style={{ width: '100%', marginTop: 10 }} data-testid="fs-detail-sync-btn">
            <RotateCw size={14} className={syncing ? 'fs-spin' : ''} /> {syncing ? 'SINCRONIZANDO...' : 'SINCRONIZAR AGORA'}
          </button>
        )}
        {flight.last_error && (
          <div style={{ fontSize: 11, color: 'var(--fs-red)', marginTop: 8 }}>Erro: {flight.last_error}</div>
        )}
      </div>

      {/* Key stats */}
      <div className="fs-grid cols-2">
        <StatCard icon={Clock} label="Duracao" value={secToMin(flight.duration_seconds)} color="var(--fs-cyan)" />
        <StatCard icon={MapPin} label="Distancia" value={`${(flight.distance_meters / 1000).toFixed(2)} km`} color="var(--fs-purple)" />
        <StatCard icon={Plane} label="Alt. maxima" value={`${(flight.max_altitude_meters || 0).toFixed(0)} m`} color="var(--fs-green)" />
        <StatCard icon={Gauge} label="Vel. maxima" value={`${(flight.max_speed_ms || 0).toFixed(1)} m/s`} color="var(--fs-amber)" />
      </div>

      {/* RTK quality - shown when CSV had RTK columns OR MRK photos uploaded */}
      {flight.rtk_summary && (flight.rtk_summary.has_rtk || flight.rtk_summary.has_rtk_photos) && (
        <RTKCard summary={flight.rtk_summary} flightId={flight.server_flight_id} ctx={ctx} />
      )}

      {/* Altitude chart */}
      {altData.length > 1 && (
        <div className="fs-card">
          <div className="fs-card-title">ALTITUDE (m)</div>
          <div style={{ height: 160 }} data-testid="fs-chart-altitude">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={altData} margin={{ top: 5, right: 8, left: -22, bottom: 0 }}>
                <CartesianGrid stroke="rgba(0,212,255,0.1)" strokeDasharray="3 3" />
                <XAxis dataKey="t" tick={{ fill: '#7d8ca3', fontSize: 10 }} />
                <YAxis tick={{ fill: '#7d8ca3', fontSize: 10 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="altitude" stroke="#00d4ff" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Speed chart */}
      {speedData.length > 1 && (
        <div className="fs-card">
          <div className="fs-card-title">VELOCIDADE (m/s)</div>
          <div style={{ height: 160 }} data-testid="fs-chart-speed">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={speedData} margin={{ top: 5, right: 8, left: -22, bottom: 0 }}>
                <CartesianGrid stroke="rgba(124,58,237,0.1)" strokeDasharray="3 3" />
                <XAxis dataKey="t" tick={{ fill: '#7d8ca3', fontSize: 10 }} />
                <YAxis tick={{ fill: '#7d8ca3', fontSize: 10 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="speed" stroke="#a78bfa" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Battery chart */}
      {batteryData.length > 1 && (
        <div className="fs-card">
          <div className="fs-card-title" style={{ color: 'var(--fs-green)' }}><Battery size={12} /> BATERIA (%)</div>
          <div style={{ height: 160 }} data-testid="fs-chart-battery">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={batteryData} margin={{ top: 5, right: 8, left: -22, bottom: 0 }}>
                <defs>
                  <linearGradient id="fsBatGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.65} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.08} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(16,185,129,0.1)" strokeDasharray="3 3" />
                <XAxis dataKey="t" tick={{ fill: '#7d8ca3', fontSize: 10 }} />
                <YAxis tick={{ fill: '#7d8ca3', fontSize: 10 }} domain={[0, 100]} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="battery" stroke="#10b981" fill="url(#fsBatGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="fs-card">
        <div className="fs-card-title">DETALHES</div>
        <div className="fs-metric-row"><span className="fs-metric-label">Decolagem</span><span className="fs-metric-value" style={{ fontSize: 11 }}>{flight.start_time ? new Date(flight.start_time).toLocaleString('pt-BR') : '-'}</span></div>
        <div className="fs-metric-row"><span className="fs-metric-label">Pouso</span><span className="fs-metric-value" style={{ fontSize: 11 }}>{flight.end_time ? new Date(flight.end_time).toLocaleString('pt-BR') : '-'}</span></div>
        <div className="fs-metric-row"><span className="fs-metric-label">Pontos GPS</span><span className="fs-metric-value">{flight.gps_track?.length || 0}</span></div>
        <div className="fs-metric-row"><span className="fs-metric-label">Bateria</span><span className="fs-metric-value">{flight.battery_start_pct ?? '-'}% → {flight.battery_end_pct ?? '-'}%</span></div>
        <div className="fs-metric-row"><span className="fs-metric-label">Piloto</span><span className="fs-metric-value">{flight.pilot_name || '-'}</span></div>
        <div className="fs-metric-row"><span className="fs-metric-label">Home</span><span className="fs-metric-value" style={{ fontSize: 11 }}>{flight.home_lat?.toFixed(5)}, {flight.home_lng?.toFixed(5)}</span></div>
      </div>

      <button className="fs-btn fs-btn-danger" onClick={handleDelete} style={{ width: '100%' }} data-testid="fs-detail-delete-btn">
        <Trash2 size={14} /> REMOVER DO DISPOSITIVO
      </button>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="fs-card" style={{ marginBottom: 0, padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <Icon size={11} style={{ color }} />
        <span style={{ fontSize: 10, letterSpacing: '0.16em', color: 'var(--fs-text-dim)', textTransform: 'uppercase' }}>{label}</span>
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace', color }}>
        {value}
      </div>
    </div>
  );
}

function RTKCard({ summary, flightId, ctx }) {
  const [uploadingMrk, setUploadingMrk] = useState(false);
  const [uploadingKml, setUploadingKml] = useState(false);
  const [mrkResult, setMrkResult] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL || '';

  // Prefer CSV-extracted RTK stats; fall back to MRK-derived stats when absent
  const fixPct = summary.fix_pct ?? summary.mrk_fix_pct ?? 0;
  const hAcc = summary.avg_h_acc_m ?? summary.mrk_avg_h_acc_m;
  const vAcc = summary.avg_v_acc_m ?? summary.mrk_avg_v_acc_m;
  const grade = fixPct >= 95 ? { label: 'EXCELENTE', color: 'var(--fs-green)' } :
                fixPct >= 80 ? { label: 'BOM', color: 'var(--fs-cyan)' } :
                fixPct >= 50 ? { label: 'REGULAR', color: 'var(--fs-amber)' } :
                                { label: 'BAIXO', color: 'var(--fs-red)' };

  const uploadFile = async (file, kind) => {
    if (!flightId) {
      toast.error('Voo ainda nao sincronizado. Aguarde a sincronizacao.');
      return;
    }
    const setter = kind === 'mrk' ? setUploadingMrk : setUploadingKml;
    setter(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const url = kind === 'mrk' ? `${API}/api/rtk/${flightId}/mrk` : `${API}/api/rtk/${flightId}/mission`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'X-Flight-Token': ctx.token },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erro');
      if (kind === 'mrk') {
        setMrkResult(data);
        toast.success(`${data.total_photos} fotos RTK importadas`);
      } else {
        toast.success(`Plano de missao importado (${data.stats?.waypoint_count || 0} waypoints)`);
      }
    } catch (e) {
      toast.error(e.message || 'Falha ao enviar');
    } finally {
      setter(false);
    }
  };

  return (
    <div className="fs-card" style={{ borderColor: 'rgba(124,58,237,0.3)', background: 'linear-gradient(180deg, rgba(124,58,237,0.05), transparent)' }} data-testid="fs-rtk-card">
      <div className="fs-card-title" style={{ color: '#a78bfa' }}>
        <Satellite size={12} /> QUALIDADE RTK
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--fs-text-dim)', textTransform: 'uppercase', letterSpacing: '0.14em' }}>FIX</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: grade.color, fontFamily: 'JetBrains Mono, monospace' }}>{fixPct}%</div>
        </div>
        <span className="fs-pill" style={{ background: `${grade.color}22`, color: grade.color }}>{grade.label}</span>
      </div>
      <div style={{ display: 'flex', gap: 8, fontSize: 11, color: 'var(--fs-text-dim)', flexWrap: 'wrap' }}>
        {summary.float_pct > 0 && <span>Float {summary.float_pct}%</span>}
        {summary.single_pct > 0 && <span>Single {summary.single_pct}%</span>}
        {hAcc && <span>H Acc {(hAcc * 100).toFixed(2)} cm</span>}
        {vAcc && <span>V Acc {(vAcc * 100).toFixed(2)} cm</span>}
        {summary.satellites_max && <span>Sats {summary.satellites_max}</span>}
        {summary.mrk_total_photos && <span>{summary.mrk_total_photos} fotos</span>}
      </div>

      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--fs-border)' }}>
        <div className="fs-card-title" style={{ marginBottom: 8 }}>
          <Camera size={11} /> FOTOS RTK (TIMESTAMP.MRK)
        </div>
        <label className="fs-btn fs-btn-ghost" style={{ width: '100%', cursor: 'pointer', justifyContent: 'center' }}>
          {uploadingMrk ? 'ENVIANDO...' : 'ENVIAR ARQUIVO MRK'}
          <input type="file" accept=".mrk,text/plain" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f, 'mrk'); e.target.value = ''; }}
                 data-testid="fs-rtk-mrk-input" />
        </label>
        {mrkResult && (
          <div className="fs-flight-meta" style={{ marginTop: 6 }}>
            ✓ {mrkResult.total_photos} fotos · FIX {mrkResult.stats?.rtk_fix_pct}%
          </div>
        )}
      </div>

      <div style={{ marginTop: 10 }}>
        <div className="fs-card-title" style={{ marginBottom: 8 }}>
          <MapIcon size={11} /> PLANO DE MISSAO (KML/KMZ)
        </div>
        <label className="fs-btn fs-btn-ghost" style={{ width: '100%', cursor: 'pointer', justifyContent: 'center' }}>
          {uploadingKml ? 'ENVIANDO...' : 'ENVIAR PLANO DJI PILOT 2'}
          <input type="file" accept=".kml,.kmz" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f, 'kml'); e.target.value = ''; }}
                 data-testid="fs-rtk-kml-input" />
        </label>
      </div>
    </div>
  );
}

function secToMin(s) {
  if (!s) return '0:00';
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${ss}`;
}

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
